import { SOP } from '../types/sop';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { firestoreWrapper } from './firestoreWrapper';

// Cache configuration
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes (increased from 5 minutes)
const CACHE_STALE_TTL = 60 * 60 * 1000; // 1 hour (stale data threshold)
const SOP_CACHE_KEY = 'sopCache';

// Store the timestamp of the last manual refresh
let lastManualRefreshTimestamp = 0;

// Function to set the manual refresh timestamp
export const setManualRefreshTimestamp = () => {
  lastManualRefreshTimestamp = Date.now();
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Load cache from localStorage
const loadCache = <T>(key: string): CacheEntry<T> | null => {
  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Save cache to localStorage
const saveCache = <T>(key: string, data: T) => {
  try {
    const cacheData: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (e) {
    // Silently fail - no need to log cache errors
  }
};

// Validate cache against last sync timestamp
const validateCache = async <T>(cachedData: CacheEntry<T> | null): Promise<CacheEntry<T> | null> => {
  if (!cachedData) return null;
  
  // If the cache is older than the last manual refresh, invalidate it
  if (lastManualRefreshTimestamp > 0 && cachedData.timestamp < lastManualRefreshTimestamp) {
    
    return null; // Cache is invalid
  }
  
  try {
    // Get the last sync timestamp from Firestore
    const syncDoc = await firestoreWrapper.getDoc(
      firestoreWrapper.doc(
        firestoreWrapper.collection('sopSync'),
        'latest'
      )
    );
    if (syncDoc.exists()) {
      const data = syncDoc.data();
      // Check for lastSync (Firestore Timestamp) or lastSyncISO (ISO string)
      let lastSyncTimestamp: number | null = null;
      
      if (data.lastSync) {
        lastSyncTimestamp = data.lastSync.toMillis ? data.lastSync.toMillis() : new Date(data.lastSync).getTime();
      } else if (data.lastSyncISO) {
        lastSyncTimestamp = new Date(data.lastSyncISO).getTime();
      }
      
      if (lastSyncTimestamp && cachedData.timestamp < lastSyncTimestamp) {
        
        return null; // Cache is invalid
      }
    }
    return cachedData; // Cache is still valid
  } catch (error) {
    
    return cachedData; // On error, return the cached data anyway
  }
};

// Memory cache for even faster access
let memoryCache: SOP[] | null = null;

export const sopCache = {
  async getSOPs(): Promise<SOP[]> {
    try {
      // Check memory cache first
      if (memoryCache) {
        return memoryCache;
      }

      // Check localStorage cache and validate against sync timestamp
      const cached = await validateCache(loadCache<SOP[]>(SOP_CACHE_KEY));
      const now = Date.now();
      if (cached && now - cached.timestamp < CACHE_TTL) {
        memoryCache = cached.data;
        return cached.data;
      }

      // Get entries from Firebase
      const querySnapshot = await firestoreWrapper.getDocs(
        firestoreWrapper.collection('sops')
      );
      
      const entries: SOP[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        // Filter out SOPs with "OLD DO NOT USE" in the title (case-insensitive)
        if (data && data.title && !data.title.toLowerCase().includes('old do not use')) {
          entries.push({
            id: doc.id,
            title: data.title,
            department: data.department,
            link: data.link,
            dateCreated: data.dateCreated,
            author1: data.author1,
            author2: data.author2,
            tags: data.tags || [],
            flaggedForReview: data.flaggedForReview || false,
            flagReason: data.flagReason || null,
            flaggedBy: data.flaggedBy || null,
            flaggedAt: data.flaggedAt || null
          });
        } else if (data && data.title && data.title.toLowerCase().includes('old do not use')) {
          // Optional: Log filtered SOPs
          // 
        }
      });

      // Save to both caches (keeping original spreadsheet order)
      memoryCache = entries;
      saveCache(SOP_CACHE_KEY, entries);
      return entries;
    } catch (error) {
      
      
      // If there's an error, return cached data if available
      if (memoryCache) {
        return memoryCache;
      }
      
      // Try to use stale cache if available
      return this.getStaleCache();
    }
  },

  // Fallback to stale cache when fresh data cannot be fetched
  async getStaleCache(): Promise<SOP[]> {
    const cached = loadCache<SOP[]>(SOP_CACHE_KEY);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_STALE_TTL) {
      
      return cached.data;
    }

    return []; // No valid cache available
  },

  clearCache() {
    
    memoryCache = null;
    localStorage.removeItem(SOP_CACHE_KEY);
    
    // Also set manual refresh timestamp to invalidate other caches
    setManualRefreshTimestamp();
  }
};
